import { ComparisonItem } from '../services/shiftService';
import { Seg, TimelineRow } from '../pages/ShiftTimeline';

/**
 * 突合の結果を「日ごと × 人ごと」の描画用の行に開く。
 *
 * ## なぜ要るのか
 *
 * 時間軸を **0:00–24:00 に固定**した（代表判断 2026-08-29）。日ごとに軸を
 * 伸縮させると、日をまたいで見比べたときに同じ位置が同じ時刻でなくなるため。
 *
 * その代わり、**日をまたぐ勤務は2日に分けて描く**必要がある。
 * 22:00–06:00 は「その日の 22:00–24:00」と「翌日の 0:00–6:00」になる。
 *
 * ⚠️ 翌日側は、その日の突合の結果に**行が無いこともある**（翌日は予定も打刻も
 * 無い場合）。だから「item を並べる」のではなく「**セグメントを日付に配る**」形で作る。
 */

const DAY = 24 * 60;

const nextDate = (date: string): string => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** 1本の範囲を日付に配る。24:00 を超えた分は翌日へ送る */
function spread(
  date: string,
  start: number,
  end: number,
  put: (date: string, seg: Seg) => void,
  open = false
) {
  if (end <= DAY) {
    put(date, { start, end, open });
    return;
  }
  put(date, { start, end: DAY, toNext: true, open });
  // 24時間を超えるほど長い勤務は想定しない。念のため翌日ぶんだけ切って捨てる
  put(nextDate(date), { start: 0, end: Math.min(end - DAY, DAY), fromPrev: true, open });
}

/**
 * 表示用に開く。戻り値は日付昇順で、日ごとに人の行が並ぶ。
 *
 * `retainDates` を渡すと、その日付だけを残す（「差のある日だけ表示」のとき、
 * 翌日へこぼれた帯のためだけに関係ない日が増えるのを防ぐ）。
 */
export function toTimelineByDate(
  items: ComparisonItem[],
  retainDates?: Set<string>
): [string, TimelineRow[]][] {
  // date -> userId -> row
  const byDate = new Map<string, Map<number, TimelineRow>>();

  const rowOf = (date: string, userId: number): TimelineRow => {
    if (!byDate.has(date)) byDate.set(date, new Map());
    const perUser = byDate.get(date)!;
    if (!perUser.has(userId)) {
      perUser.set(userId, { userId, upper: [], lower: [] });
    }
    return perUser.get(userId)!;
  };

  for (const it of items) {
    for (const r of it.plannedRanges) {
      spread(it.date, r.start, r.end, (d, seg) => rowOf(d, it.userId).upper.push(seg));
    }
    for (const r of it.actualRanges) {
      /*
       * 退勤が未打刻の帯は **短く薄く** 描く（長さに意味は無い）。終わりが分からない
       * ものを長く描くと、その時刻まで働いたように読める。凡例で読み方を出す
       * （`ShiftTimeline` の `hasOpen`）。
       */
      const open = r.end === null;
      const end = r.end ?? Math.min(r.start + 60, DAY);
      spread(it.date, r.start, end, (d, seg) => rowOf(d, it.userId).lower.push(seg), open);
    }
    // 帯が無い理由が読めないのはこの場合だけ。他の状態は文字で出さない
    if (it.status === 'missing_punch') rowOf(it.date, it.userId).note = '打刻なし';
    /*
     * ⚠️ **下書きは突合には使わないが、帯としては描く**（代表判断 2026-08-29）。
     * 「何も予定が無かった」のか「組んであるが未確定」なのかは、見えた方がよい。
     * 破線で描き、行にも「下書き（未確定）」と出す。
     */
    for (const r of it.draftRanges ?? []) {
      spread(it.date, r.start, r.end, (d, seg) => rowOf(d, it.userId).upper.push({ ...seg, draft: true }));
    }
    if (it.hasDraft && it.plannedRanges.length === 0) {
      const row = rowOf(it.date, it.userId);
      row.note = row.note && row.note !== '打刻なし' ? row.note : '下書き（未確定）';
      row.noteColor = '#92400E';
    }
    // 予定も実績も無い日（公休など）でも、行だけは作らない。帯が無い行は情報を持たない
  }

  return [...byDate.entries()]
    .filter(([date]) => !retainDates || retainDates.has(date))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, perUser]) => [
      date,
      [...perUser.values()].filter((r) => r.upper.length > 0 || r.lower.length > 0 || r.note),
    ]);
}
